import Button from "common/components/Button/Button";

import { Animal } from "types/Animal";
import { Link } from "react-router-dom";
import useAuth from "common/hooks/useAuth";
import { useTranslation } from "react-i18next";
import ImageComponent from "common/components/ImageComponent/ImageComponent";
import ImageFromStrapiMedia from "common/components/ImageFromStrapiMedia/ImageFromStrapiMedia";
import { useMemo } from "react";
import { TableCellsIcon } from "@heroicons/react/24/solid";
import TooltipWrapper from "common/components/TooltipWrapper/TooltipWrapper";
import { ObservationGrid } from "types/ObservationGrid";
import _ from "lodash";
import getAnimalLabel from "utils/animalLabel";
import { Tag } from "types/Tag";
import { PiTagLight } from "react-icons/pi";
import dayjs from "dayjs";

function AnimalItem({ item, onDelete, onNavigate }: any) {
  const { user: currentUser } = useAuth();
  const canSeeDetailsFromItem =
    (currentUser?.isSuperadmin ||
      (currentUser?.isAdmin &&
        Boolean(currentUser?.park.id === item.park.id)) ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        Boolean(currentUser?.park.id === item.park.id) &&
        currentUser?.administrationPermissions?.animal?.read)) ??
    false;
  const canEditItem =
    (currentUser?.isSuperadmin ||
      (currentUser?.isAdmin &&
        Boolean(currentUser?.park.id === item.park.id)) ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        Boolean(currentUser?.park.id === item.park.id) &&
        currentUser?.administrationPermissions?.animal?.update)) ??
    false;

  const canDeleteItem =
    (currentUser?.isSuperadmin ||
      (currentUser?.isAdmin &&
        Boolean(currentUser?.park.id === item.park.id)) ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        Boolean(currentUser?.park.id === item.park.id) &&
        currentUser?.administrationPermissions?.animal?.delete)) ??
    false;

  const {
    id,
    houseName,
    picture,
    gender,
    species,
    zimsId,
    isGroup,
    localId,
    tag,
    isArchived,
    tags,
    birthDate,
    estimatedBirthDate,
  } = item as Animal;

  const detailLink = `/animals/${item.id}`;

  const { t } = useTranslation();

  const observationGrids = useMemo(() => {
    const items = [
      ...(species?.observationGrids ?? []),
      ...(species?.genus?.superfamily?.observationGrids ?? []),
    ];
    const itemsIds = _.uniq(items?.map((i) => i.id));
    return itemsIds?.map((i) => items.find((a) => a.id === i));
  }, [species]);

  const containerColor = isArchived ? "opacity-65" : "";

  return (
    <div
      className={`flex px-4 py-4 mt-2 w-full relative bg-white cursor-pointer rounded-lg border border-slate-150 transition-all ${containerColor}`}
    >
      <Link
        to={detailLink}
        className={
          !canSeeDetailsFromItem
            ? "pointer-events-none flex flex-1"
            : "flex flex-1"
        }
      >
        <div className="flex relative w-40 h-40 lg:w-16 lg:h-16 shrink-0">
          <div className="rounded-lg overflow-hidden w-full">
            <ImageComponent
              image={picture ? ImageFromStrapiMedia(picture)?.uri : undefined}
            />
          </div>
        </div>
        <div className="flex flex-col flex-1 justify-between pl-4 pr-6 mb-2">
          <div className="flex gap-3">
            <div className="flex-1 flex flex-col gap-2 py-3 lg:py-0">
              <div className="flex flex-row align-center gap-3">
                <h4 className="text-xl font-bold text-primary leading-4">
                  {isGroup && `${t("common.individualsGroup")} : `}
                  {getAnimalLabel(id, houseName, zimsId, localId, tag)}
                </h4>
                {!isGroup && gender && (
                  <div className="px-2 rounded-md bg-green-100 text-green-800 border border-green-500 flex flex-col justify-center items-center text-sm w-fit">
                    {t(`animals.${gender}`)}
                  </div>
                )}
              </div>

              {species && species.scientificName && species.name ? (
                <div className="flex flex-row items-center text-sm gap-1">
                  <span className="font-bold italic">
                    {species.scientificName}
                  </span>
                  <span className="text-gray-500">{`(${species.name})`}</span>
                </div>
              ) : species ? (
                <div className="flex flex-row items-center text-sm">
                  <span className="font-bold">
                    {species.scientificName ?? species.name}
                  </span>
                </div>
              ) : null}

              {Boolean(estimatedBirthDate || birthDate) && (
                <div className="flex items-center gap-1 text-sm">
                  <span className="font-bold">
                    {t("import.birthDate")}
                    {": "}
                    {dayjs(estimatedBirthDate || birthDate).format(
                      "DD-MM-YYYY"
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </Link>

      <div className="flex flex-col justify-end items-end text-xs lg:border-l lg:pl-4 lg:min-w-[22em] border-slate-150 gap-2">
        <div className="w-full h-full">
          {zimsId && (
            <div className="flex items-center gap-1 text-sm">
              <span className="font-normal">{`${t("animals.zimsId")}: `}</span>
              <span className="font-bold">{zimsId}</span>
            </div>
          )}
          {localId && (
            <div className="flex items-center gap-1 text-sm">
              <span className="font-normal">{`${t("animals.localId")}: `}</span>
              <span className="font-bold">{localId}</span>
            </div>
          )}
        </div>
        {tags?.length > 0 && (
          <TooltipWrapper
            className="mr-1"
            content={
              tags?.length > 0 ? (
                <div className="flex flex-col flex-wrap gap-1">
                  {tags?.map((tagItem: Tag | undefined) =>
                    tagItem ? (
                      <span className="font-bold">{tagItem.name}</span>
                    ) : null
                  )}
                </div>
              ) : null
            }
          >
            <div className="flex items-center gap-1 text-sm">
              <PiTagLight className="w-3 h-3" />
              <span className="font-bold">
                {t("forms.childrenTagsLength", {
                  count: tags?.length || 0,
                })}
              </span>
            </div>
          </TooltipWrapper>
        )}
        <TooltipWrapper
          className="mr-1"
          content={
            observationGrids?.length > 0 ? (
              <div className="flex flex-col flex-wrap gap-1">
                {observationGrids?.map((grid: ObservationGrid | undefined) =>
                  grid ? <span className="font-bold">{grid.name}</span> : null
                )}
              </div>
            ) : null
          }
        >
          <div className="flex items-center gap-1 text-sm">
            <TableCellsIcon className="w-3 h-3" />
            <span className="font-bold">
              {t("forms.observationGridsLength", {
                count: observationGrids?.length || 0,
              })}
            </span>
          </div>
        </TooltipWrapper>

        <div className="flex w-full items-center justify-end text-slate-500">
          <div>
            <div className="flex w-full gap-1">
              <TooltipWrapper
                content={
                  <span>
                    {isArchived ? t("common.restore") : t("common.archived")}
                  </span>
                }
              >
                <Button
                  type="error-line"
                  icon={
                    isArchived ? "RiInboxUnarchiveLine" : "RiInboxArchiveLine"
                  }
                  compact
                  onClick={() => onDelete(item.id)}
                  disabled={!canDeleteItem}
                  confirmMessage={
                    isArchived
                      ? t("actions.confirmRestore")
                      : t("actions.confirmArchive")
                  }
                />
              </TooltipWrapper>
              <Button
                title={t("actions.edit")}
                type="warning-line"
                icon="PencilSquareIcon"
                onClick={() => onNavigate(item.id)}
                disabled={!canEditItem}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AnimalItem;
